/**
 * Analytics API - Google Analytics integration
 */
import apiClient from './index'
import type { ApiResponse } from '@/types'

// Connection status
export interface AnalyticsStatus {
  connected: boolean
  property_id: string | null
  using_mock_data: boolean
  credentials_source?: 'database' | 'environment' | null
}

// Common query parameters
export interface AnalyticsQueryParams {
  days?: number
  start_date?: string
  end_date?: string
  compare_to?: 'previous_period' | 'previous_year'
}

export interface AnalyticsMetric {
  name: string
  value: number
  change_percent: number | null
  previous_value: number | null
  yoy_change_percent: number | null
  yoy_previous_value: number | null
}

export interface TrafficSource {
  source_medium: string
  sessions: number
  users: number
  conversions: number
  bounce_rate: number
  prev_sessions?: number
  sessions_change?: number
}

export interface TopPage {
  page: string
  pageviews: number
  avg_time: number
  bounce_rate: number
  users: number
}

export interface Campaign {
  campaign: string
  source: string
  sessions: number
  users: number
  conversions: number
  bounce_rate: number
}

export interface TimeSeriesPoint {
  date: string
  users: number
  sessions: number
  pageviews: number
  conversions?: number
}

export interface TimeSeriesData {
  current: TimeSeriesPoint[]
  current_label: string
  comparison?: TimeSeriesPoint[]
  comparison_label?: string
  comparison_type?: 'previous_period' | 'previous_year'
}

export interface DeviceBreakdown {
  device: string
  sessions: number
  users: number
}

export interface GeoBreakdown {
  country: string
  sessions: number
  users: number
  conversions?: number
}

export interface BrowserBreakdown {
  browser: string
  sessions: number
  users: number
  bounce_rate: number
}

export interface OsBreakdown {
  os: string
  sessions: number
  users: number
}

export interface LandingPage {
  page: string
  sessions: number
  users: number
  bounce_rate: number
  conversions: number
}

export interface ExitPage {
  page: string
  exits: number
  pageviews: number
  exit_rate: number
}

export interface UserType {
  user_type: 'new' | 'returning'
  sessions: number
  users: number
  conversions: number
}

export interface CityBreakdown {
  city: string
  country: string
  sessions: number
  users: number
}

export interface EventData {
  event: string
  count: number
  users: number
}

export interface ConversionGoal {
  goal: string
  conversions: number
  users: number
}

export interface RealtimeData {
  active_users: number
  top_pages: Array<{ page: string; users: number }>
  devices: Array<{ device: string; users: number }>
  countries: Array<{ country: string; users: number }>
}

export interface AnalyticsOverview {
  date_range: string
  start_date: string
  end_date: string
  comparison_type: string | null
  metrics: AnalyticsMetric[]
}

export interface AnalyticsDashboard {
  overview: AnalyticsOverview
  traffic_sources: TrafficSource[]
  top_pages: TopPage[]
  campaigns: Campaign[]
  time_series: TimeSeriesData
  devices: DeviceBreakdown[]
  geo: GeoBreakdown[]
}

// Detail views
export interface PageDetail {
  page: string
  date_range: string
  metrics: {
    pageviews: number
    users: number
    avg_time: number
    bounce_rate: number
    entrances: number
    exits: number
  }
  time_series: Array<{
    date: string
    pageviews: number
    users: number
  }>
  traffic_sources: Array<{
    source_medium: string
    pageviews: number
    users: number
  }>
}

export interface SourceDetail {
  source_medium: string
  date_range: string
  metrics: {
    sessions: number
    users: number
    pageviews: number
    avg_time: number
    bounce_rate: number
    conversions: number
  }
  time_series: Array<{
    date: string
    sessions: number
    users: number
  }>
  landing_pages: Array<{
    page: string
    sessions: number
    users: number
  }>
}

export interface CampaignDetail {
  campaign: string
  date_range: string
  metrics: {
    sessions: number
    users: number
    new_users: number
    pageviews: number
    avg_time: number
    bounce_rate: number
    conversions: number
  }
  time_series: Array<{
    date: string
    sessions: number
    conversions: number
  }>
  sources: Array<{
    source: string
    sessions: number
    conversions: number
  }>
}

// Secondary dimension data for drill-down
export interface SecondaryDimensionData {
  dimension: string
  sessions?: number
  users: number
  pageviews?: number
  conversions?: number
  percentage: number
}

// Helper to build export URL with params
function buildExportUrl(endpoint: string, params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv' }): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1'
  const url = new URL(`${baseUrl}${endpoint}`, window.location.origin)

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value))
      }
    })
  }

  return url.toString()
}

export const analyticsApi = {
  /**
   * Get analytics connection status
   */
  async getStatus(): Promise<AnalyticsStatus> {
    const response = await apiClient.get<ApiResponse<AnalyticsStatus>>(
      '/analytics/status'
    )
    return response.data.data
  },

  /**
   * Get all analytics data for dashboard
   */
  async getDashboard(params?: AnalyticsQueryParams): Promise<AnalyticsDashboard> {
    const response = await apiClient.get<ApiResponse<AnalyticsDashboard>>(
      '/analytics/dashboard',
      { params }
    )
    return response.data.data
  },

  /**
   * Get overview metrics with comparison
   */
  async getOverview(params?: AnalyticsQueryParams): Promise<AnalyticsOverview> {
    const response = await apiClient.get<ApiResponse<AnalyticsOverview>>(
      '/analytics/overview',
      { params }
    )
    return response.data.data
  },

  /**
   * Get traffic sources breakdown
   */
  async getTrafficSources(params?: AnalyticsQueryParams): Promise<TrafficSource[]> {
    const response = await apiClient.get<ApiResponse<{ values: TrafficSource[] }>>(
      '/analytics/traffic-sources',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get top pages
   */
  async getTopPages(params?: AnalyticsQueryParams & { limit?: number }): Promise<TopPage[]> {
    const response = await apiClient.get<ApiResponse<{ values: TopPage[] }>>(
      '/analytics/top-pages',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get campaign performance
   */
  async getCampaigns(params?: AnalyticsQueryParams): Promise<Campaign[]> {
    const response = await apiClient.get<ApiResponse<{ values: Campaign[] }>>(
      '/analytics/campaigns',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get traffic over time with optional comparison
   */
  async getTrafficOverTime(
    params?: AnalyticsQueryParams & { granularity?: 'day' | 'week' | 'month' }
  ): Promise<TimeSeriesData> {
    const response = await apiClient.get<ApiResponse<TimeSeriesData>>(
      '/analytics/traffic-over-time',
      { params }
    )
    return response.data.data
  },

  /**
   * Get device breakdown
   */
  async getDevices(params?: AnalyticsQueryParams): Promise<DeviceBreakdown[]> {
    const response = await apiClient.get<ApiResponse<{ values: DeviceBreakdown[] }>>(
      '/analytics/devices',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get geographic breakdown
   */
  async getGeo(params?: AnalyticsQueryParams & { limit?: number }): Promise<GeoBreakdown[]> {
    const response = await apiClient.get<ApiResponse<{ values: GeoBreakdown[] }>>(
      '/analytics/geo',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get browser breakdown
   */
  async getBrowsers(params?: AnalyticsQueryParams & { limit?: number }): Promise<BrowserBreakdown[]> {
    const response = await apiClient.get<ApiResponse<{ values: BrowserBreakdown[] }>>(
      '/analytics/browsers',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get operating system breakdown
   */
  async getOperatingSystems(params?: AnalyticsQueryParams & { limit?: number }): Promise<OsBreakdown[]> {
    const response = await apiClient.get<ApiResponse<{ values: OsBreakdown[] }>>(
      '/analytics/operating-systems',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get landing pages
   */
  async getLandingPages(params?: AnalyticsQueryParams & { limit?: number }): Promise<LandingPage[]> {
    const response = await apiClient.get<ApiResponse<{ values: LandingPage[] }>>(
      '/analytics/landing-pages',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get exit pages
   */
  async getExitPages(params?: AnalyticsQueryParams & { limit?: number }): Promise<ExitPage[]> {
    const response = await apiClient.get<ApiResponse<{ values: ExitPage[] }>>(
      '/analytics/exit-pages',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get user types (new vs returning)
   */
  async getUserTypes(params?: AnalyticsQueryParams): Promise<UserType[]> {
    const response = await apiClient.get<ApiResponse<{ values: UserType[] }>>(
      '/analytics/user-types',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get city breakdown
   */
  async getCities(params?: AnalyticsQueryParams & { limit?: number }): Promise<CityBreakdown[]> {
    const response = await apiClient.get<ApiResponse<{ values: CityBreakdown[] }>>(
      '/analytics/cities',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get events data
   */
  async getEvents(params?: AnalyticsQueryParams & { limit?: number }): Promise<EventData[]> {
    const response = await apiClient.get<ApiResponse<{ values: EventData[] }>>(
      '/analytics/events',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get conversions breakdown
   */
  async getConversions(params?: AnalyticsQueryParams): Promise<ConversionGoal[]> {
    const response = await apiClient.get<ApiResponse<{ values: ConversionGoal[] }>>(
      '/analytics/conversions',
      { params }
    )
    return response.data.data.values
  },

  /**
   * Get realtime data
   */
  async getRealtime(): Promise<RealtimeData> {
    const response = await apiClient.get<ApiResponse<RealtimeData>>(
      '/analytics/realtime'
    )
    return response.data.data
  },

  // Drill-down endpoints

  /**
   * Get detailed analytics for a specific page
   */
  async getPageDetail(pagePath: string, params?: AnalyticsQueryParams): Promise<PageDetail> {
    // Encode the page path for URL
    const encodedPath = encodeURIComponent(pagePath.replace(/^\//, ''))
    const response = await apiClient.get<ApiResponse<PageDetail>>(
      `/analytics/pages/${encodedPath}`,
      { params }
    )
    return response.data.data
  },

  /**
   * Get detailed analytics for a specific traffic source
   */
  async getSourceDetail(sourceMedium: string, params?: AnalyticsQueryParams): Promise<SourceDetail> {
    const encodedSource = encodeURIComponent(sourceMedium)
    const response = await apiClient.get<ApiResponse<SourceDetail>>(
      `/analytics/sources/${encodedSource}`,
      { params }
    )
    return response.data.data
  },

  /**
   * Get detailed analytics for a specific campaign
   */
  async getCampaignDetail(campaignName: string, params?: AnalyticsQueryParams): Promise<CampaignDetail> {
    const encodedCampaign = encodeURIComponent(campaignName)
    const response = await apiClient.get<ApiResponse<CampaignDetail>>(
      `/analytics/campaigns/${encodedCampaign}`,
      { params }
    )
    return response.data.data
  },

  /**
   * Get secondary dimension data for drill-down analytics
   * This enables Universal Analytics-style slice and dice functionality
   */
  async getSecondaryDimension(
    primaryType: 'page' | 'source' | 'campaign',
    primaryValue: string,
    secondaryDimension: string,
    params?: AnalyticsQueryParams
  ): Promise<SecondaryDimensionData[]> {
    const encodedValue = encodeURIComponent(primaryValue)
    const response = await apiClient.get<ApiResponse<{ values: SecondaryDimensionData[] }>>(
      `/analytics/${primaryType}s/${encodedValue}/dimension/${secondaryDimension}`,
      { params }
    )
    return response.data.data.values
  },

  // Export endpoints - these return download URLs

  /**
   * Get export URL for full dashboard data
   */
  getExportDashboardUrl(params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv' }): string {
    return buildExportUrl('/analytics/export/dashboard', params)
  },

  /**
   * Get export URL for traffic sources
   */
  getExportTrafficSourcesUrl(params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv' }): string {
    return buildExportUrl('/analytics/export/traffic-sources', params)
  },

  /**
   * Get export URL for top pages
   */
  getExportTopPagesUrl(params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv', limit?: number }): string {
    return buildExportUrl('/analytics/export/top-pages', params)
  },

  /**
   * Get export URL for campaigns
   */
  getExportCampaignsUrl(params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv' }): string {
    return buildExportUrl('/analytics/export/campaigns', params)
  },

  /**
   * Get export URL for geographic data
   */
  getExportGeoUrl(params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv', limit?: number }): string {
    return buildExportUrl('/analytics/export/geo', params)
  },

  /**
   * Get export URL for traffic over time
   */
  getExportTrafficOverTimeUrl(params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv', granularity?: string }): string {
    return buildExportUrl('/analytics/export/traffic-over-time', params)
  },

  /**
   * Get export URL for page detail
   */
  getExportPageDetailUrl(pagePath: string, params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv' }): string {
    const encodedPath = encodeURIComponent(pagePath.replace(/^\//, ''))
    return buildExportUrl(`/analytics/export/page/${encodedPath}`, params)
  },

  /**
   * Get export URL for source detail
   */
  getExportSourceDetailUrl(sourceMedium: string, params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv' }): string {
    const encodedSource = encodeURIComponent(sourceMedium)
    return buildExportUrl(`/analytics/export/source/${encodedSource}`, params)
  },

  /**
   * Get export URL for campaign detail
   */
  getExportCampaignDetailUrl(campaignName: string, params?: AnalyticsQueryParams & { format?: 'xlsx' | 'csv' }): string {
    const encodedCampaign = encodeURIComponent(campaignName)
    return buildExportUrl(`/analytics/export/campaign/${encodedCampaign}`, params)
  },

  /**
   * Trigger download for any export URL
   */
  downloadExport(url: string): void {
    // Create a hidden link and trigger download
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  },
}
